import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        // Every variant carries a translucent BLACK border in both the
        // resting and hover state. Translucent black darkens whatever is
        // beneath it — white, a vivid solid fill, or a pale tint alike — so
        // it reads as a visible hairline no matter what background color a
        // caller passes via `className` (bg-blue-600, hover:bg-red-50,
        // etc.). A border that tries to match/contrast a *specific* fill
        // (e.g. white or a theme color) breaks the moment an override
        // changes that fill; translucent black never does. Hover uses a
        // slightly stronger opacity so the border visibly reacts on hover
        // without ever disappearing.
        //
        // IMPORTANT: no variant below ever changes TEXT color on hover.
        // `outline`/`ghost` used to hover to `hover:text-accent-foreground`
        // (white), which only stays readable if a caller's override also
        // happens to flip the hover background to something dark. Every
        // per-instance override that only touched `hover:bg-*` (e.g.
        // `hover:bg-gray-100`) silently inherited that white hover text —
        // rendering it invisible against a light hover background (this is
        // exactly what caused the disappearing "Refresh" button text). By
        // only ever adjusting border/background opacity on hover — never
        // text — whatever text color a caller sets (or the default inherits)
        // stays exactly as visible on hover as it is at rest, no matter what
        // background a per-instance className overrides it to.
        default:
          'border border-black/15 bg-primary text-primary-foreground hover:border-black/25 hover:bg-primary/90',
        destructive:
          'border border-black/15 bg-destructive text-destructive-foreground hover:border-black/25 hover:bg-destructive/90',
        outline:
          'border border-input bg-background hover:border-black/25 hover:bg-black/5',
        secondary:
          'border border-black/10 bg-secondary text-secondary-foreground hover:border-black/20 hover:bg-secondary/80',
        ghost: 'border border-black/10 hover:border-black/20 hover:bg-black/5',
        link: 'border border-transparent text-primary underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-10 px-4 py-2',
        sm: 'h-9 rounded-md px-3',
        lg: 'h-11 rounded-md px-8',
        icon: 'h-10 w-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

export interface ButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
